/** @jsxImportSource @emotion/react */
import css, { SerializedStyles } from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import { Select, Spin } from 'antd'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import { paragraphMedium, weightRegular } from 'src/components/css'
import { ErrorLabel } from '../ErrorLabel'
import Text from '../Text'
import { useCountries } from '@sportninja/common/hooks/useCountries'

/**
 * CountrySelectInput component for selecting countries with search functionality
 * @component
 */
type CountrySelectInputProps = {
  /** Field name for error handling and identification */
  name: string
  /** Current selected country value (iso_3166_2 code) */
  value: string | undefined
  /** Function to update the selected country value */
  onChange: (value: string | undefined) => void
  /** Object containing field validation errors */
  fieldErrors?: any
  /** Custom error messages */
  customError?: any
  /** Label text displayed above the select */
  label?: string
  /** Placeholder text */
  placeholder?: string
  /** Whether to allow clearing the selection */
  allowClear?: boolean
  /** Whether the select is disabled */
  disabled?: boolean
  /** Whether to show search functionality */
  showSearch?: boolean
  /** Tab index for keyboard navigation */
  tabIndex?: number
  /** Custom CSS styles using emotion css */
  style?: SerializedStyles
  /** Whether the field is required (shows asterisk after label) */
  required?: boolean
}

const filterOption = (input: string, option: any) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

export const CountrySelectInput: React.FC<CountrySelectInputProps> = ({
  name,
  value,
  onChange,
  fieldErrors = {},
  customError = {},
  label = 'Country',
  placeholder = 'Select a country',
  allowClear = false,
  disabled = false,
  showSearch = true,
  tabIndex,
  style,
  required = false,
}) => {
  const { countries, loading, error, refetch } = useCountries()

  // Track dropdown open state and search value for Tab-to-select
  const [isOpen, setIsOpen] = useState(false)
  const searchRef = useRef('')

  // Sort countries: US first, then Canada, then rest alphabetically by name
  const sortedCountries = useMemo(() => {
    if (!countries.length) {
      return countries
    }

    return [...countries].sort((a, b) => {
      // US always first
      if (a.iso_3166_2 === 'US') {
        return -1
      }
      if (b.iso_3166_2 === 'US') {
        return 1
      }
      // Canada second
      if (a.iso_3166_2 === 'CA') {
        return -1
      }
      if (b.iso_3166_2 === 'CA') {
        return 1
      }
      // Rest alphabetically by name
      return a.name.localeCompare(b.name)
    })
  }, [countries])

  // Fetch countries on mount if not already loaded
  useEffect(() => {
    if (countries.length === 0) {
      refetch()
    }
  }, [countries.length, refetch])

  // Log errors
  useEffect(() => {
    if (error) {
      console.error('🗺️ CountrySelectInput: fetch error', error)
    }
  }, [error])

  // Build options array with memoization
  const countryOptions = useMemo(() => {
    return sortedCountries.map((country) => ({
      label: country.name,
      value: country.iso_3166_2,
    }))
  }, [sortedCountries])

  // Tab-to-select handler
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && isOpen && searchRef.current) {
      const matching = countryOptions.find((opt) =>
        filterOption(searchRef.current, opt)
      )
      if (matching) {
        e.preventDefault()
        handleChange(matching.value)
        setIsOpen(false)
      }
    }
  }

  // Handle change to validate that only valid country codes are accepted
  const handleChange = (newValue: string | undefined) => {
    // If clearing the selection
    if (newValue === undefined || newValue === null) {
      onChange(undefined)
      return
    }

    // Check if the value is a valid country code
    const isValidCountry = sortedCountries.some(
      (country) => country.iso_3166_2 === newValue
    )

    if (isValidCountry) {
      onChange(newValue)
    }
    // Don't update if invalid - keep the current valid selection
  }

  return (
    <>
      <style>
        {`
          .ant-select-dropdown {
            z-index: 999999 !important;
          }
          .ant-select-selector {
            background: ${colors.SECONDARY_200} !important;
            border-radius: 8px !important;
          }
          .ant-select-selection-item {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
          .ant-select-dropdown .ant-select-item-option-content {
            font-size: 14px !important;
            font-weight: 400 !important;
            color: ${colors.WHITE} !important;
          }
        `}
      </style>
      <Text
        variant='paragraphSmall'
        weight='regular'
        color={colors.WHITE}
        style={`
          align-self: stretch;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          word-break: break-word;
        `}
      >
        {label}
        {required && (
          <span
            css={css`
              margin-left: 4px;
            `}
          >
            *
          </span>
        )}
      </Text>
      <Select
        placeholder={placeholder}
        allowClear={allowClear}
        disabled={disabled || loading}
        showSearch={showSearch}
        onChange={handleChange}
        options={countryOptions}
        value={value || undefined}
        tabIndex={tabIndex}
        filterOption={filterOption}
        optionFilterProp='label'
        loading={loading}
        mode={undefined}
        open={isOpen}
        onDropdownVisibleChange={setIsOpen}
        onSearch={(val) => {
          searchRef.current = val
        }}
        onKeyDown={handleKeyDown}
        notFoundContent={
          loading ? (
            <div
              css={css`
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 8px;
              `}
            >
              <Spin size='small' />
            </div>
          ) : (
            'No countries found'
          )
        }
        css={css`
          ${paragraphMedium}
          ${weightRegular}
          font-size: 16px !important;
          color: ${colors.WHITE};
          width: 100%;
          background: ${colors.SECONDARY_200} !important;
          border-radius: 8px !important;
          height: 40px !important;
          ${disabled && 'opacity: 0.5 !important'}
          ${style}
        `}
        status={
          fieldErrors[name] || fieldErrors?.includes?.(name) ? 'error' : ''
        }
      />
      <ErrorLabel
        fieldErrors={fieldErrors || {}}
        customError={customError || {}}
        name={name}
      />
    </>
  )
}

export default CountrySelectInput
